import type { jsPDF } from "jspdf";
import type { CapturedBlock } from "./capture";
import { drawPdfTable, planColumns, type PdfTableModel } from "./pdfTable";
import {
  drawLines,
  drawTextBlock,
  FAINT,
  INK,
  lineHeightMm,
  MUTED,
  RULE,
  setFont,
  wrapText,
  type Flow,
  type TextLine,
} from "./pdfText";

/**
 * One share PDF — a DOCUMENT, not a screenshot (owner, 2026-10-22: "multi
 * page detailed pdf please, not these screenshots"): a title and the filters
 * it was cut with, then its sections in order, each laid out on A4 pages:
 *   - a TABLE (the custom report's data, a report card's <table>) is set as
 *     real text by share/pdfTable — every row, every column, the header on
 *     every page, the Total row on every page, the label columns repeated
 *     when a table is wider than the page;
 *   - a picture (a chart, a KPI strip, a card header) is captured as it is on
 *     screen and placed at screen size, never shrunk below 1:1 to fit a page
 *     of other things, and sliced between pages on a quiet row;
 *   - plain text (notes, the honesty sentence) flows across pages.
 * A DOM block (a whole card) is split into those by capture/segmentBlock.
 *
 * Two layouts. FLOW (every report card) runs the sections down one set of A4
 * pages. BOOK (the custom report; owner, 2026-10-26: "don't put all the
 * matrixes under it, make that share pdf a little bit more interactive")
 * opens on a cover with a CONTENTS list whose lines jump to their chapter,
 * starts every chapter on a page of its own, sizes a chapter's pages to its
 * widest table so a table never splits into column bands that repeat every
 * row one after another, and lists the chapters as the PDF's bookmarks.
 *
 * jsPDF and html2canvas load on the first share (dynamic import): this file
 * sits behind every report card's header, and the Dashboard shares the cards.
 */

export interface PreparedPdf {
  blob: Blob;
  fileName: string;
  title: string;
  /** the filter lines printed under the title — reused in the WhatsApp text */
  summary: string[];
  pageCount: number;
}

/** BOOK layout: a section naming a `chapter` starts one (a page of its own,
 *  a contents line, a bookmark); the sections after it, up to the next
 *  chapter, follow it on its pages. Ignored by the flow layout. */
interface Chaptered {
  chapter?: string;
}

export type PdfSection =
  /** A block on screen: split into its pictures and its tables. */
  | ({ kind: "dom"; element: HTMLElement } & Chaptered)
  /** A table from data (the custom report), with an optional title and a
   *  small note line above it. */
  | ({ kind: "table"; table: PdfTableModel; title?: string; note?: string } & Chaptered)
  /** Text lines under an optional heading. */
  | ({ kind: "text"; heading?: string; lines: string[] } & Chaptered);

export interface ReportPdfInput {
  title: string;
  summary: string[];
  preparedBy?: string | null;
  /** Sugar: on-screen blocks, in order — the same as `dom` sections. */
  blocks?: HTMLElement[];
  sections?: PdfSection[];
  /** "flow" (default) or "book" — see the header. */
  layout?: "flow" | "book";
  /** Book layout: one line under the title on the cover (how to use it). */
  hint?: string;
  onProgress?: (done: number, total: number) => void;
}

const MARGIN_MM = 12;
const GAP_MM = 5;
const FOOTER_MM = 6;
/** One CSS px at 96 dpi: a picture is never blown up past screen size. */
const MAX_MM_PER_PX = 25.4 / 96;
const PORTRAIT_CONTENT_MM = 210 - MARGIN_MM * 2;
const LANDSCAPE_CONTENT_MM = 297 - MARGIN_MM * 2;
/** Book layout: the widest a chapter's page grows for its table. Past it a
 *  table of well over a hundred columns bands after all. */
const MAX_BOOK_CONTENT_MM = 1200 - MARGIN_MM * 2;
/** Contents lines: the CRM's own green, so they read as tappable. */
const LINK_INK: [number, number, number] = [21, 101, 72]; // #156548

type Unit = (
  | { kind: "image"; block: CapturedBlock }
  | { kind: "table"; table: PdfTableModel; title?: string; note?: string }
  | { kind: "text"; heading?: string; lines: string[] }
) &
  Chaptered;

/**
 * Where to end a slice that has to split a tall picture across pages: the
 * lowest "quiet" pixel row (few sharp colour edges — the gap between table
 * rows or chart sections) in the bottom 20% of the slice, so a row of text is
 * not sliced in half. Falls back to the full slice.
 */
function quietCut(canvas: HTMLCanvasElement, srcY: number, slicePx: number): number {
  const window = Math.min(Math.round(slicePx * 0.2), 260);
  if (window < 8) return slicePx;
  try {
    const ctx = canvas.getContext("2d");
    if (!ctx) return slicePx;
    const top = srcY + slicePx - window;
    const { data, width } = ctx.getImageData(0, top, canvas.width, window);
    const maxEdges = Math.max(6, Math.round(width * 0.004));
    let best = window - 1;
    let bestEdges = Number.POSITIVE_INFINITY;
    for (let row = window - 1; row >= 0; row--) {
      let edges = 0;
      const base = row * width * 4;
      for (let x = 2; x < width; x += 2) {
        const i = base + x * 4;
        const j = i - 8;
        if (Math.abs(data[i]! - data[j]!) + Math.abs(data[i + 1]! - data[j + 1]!) + Math.abs(data[i + 2]! - data[j + 2]!) > 60) {
          edges++;
          if (edges > bestEdges) break;
        }
      }
      if (edges <= maxEdges) return slicePx - window + row + 1;
      if (edges < bestEdges) {
        bestEdges = edges;
        best = row;
      }
    }
    return slicePx - window + best + 1;
  } catch {
    // A tainted canvas (cross-origin image) can't be read — cut plainly.
    return slicePx;
  }
}

function sliceToJpeg(canvas: HTMLCanvasElement, srcY: number, heightPx: number): string {
  const out = document.createElement("canvas");
  out.width = canvas.width;
  out.height = Math.max(1, heightPx);
  const ctx = out.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, srcY, canvas.width, heightPx, 0, 0, canvas.width, heightPx);
  return out.toDataURL("image/jpeg", 0.92);
}

// A macrotask, not requestAnimationFrame: rAF never fires in a background
// tab, so a PDF started before switching tabs would hang until the user came
// back. This still lets the progress toast repaint between blocks.
const yieldToBrowser = () => new Promise<void>((r) => setTimeout(r, 0));

export function istStamp(date = new Date()): string {
  return `${new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)} IST`;
}

/** "Stage breakdown" → "Stage-breakdown-2026-10-20.pdf" */
export function reportPdfFileName(title: string, date = new Date()): string {
  const ymd = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(date);
  const slug = title
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return `${slug || "Report"}-${ymd}.pdf`;
}

const hasRows = (t: PdfTableModel) => t.head.length + t.body.length + (t.foot?.length ?? 0) > 0;

/** Place one captured picture from y down, slicing it across pages. */
function drawImage(pdf: jsPDF, block: CapturedBlock, x: number, y: number, contentW: number, flow: Flow): number {
  const mmPerPx = Math.min(contentW / block.cssWidth, MAX_MM_PER_PX);
  const wMm = block.cssWidth * mmPerPx;
  const totalMm = (block.canvas.height / block.scale) * mmPerPx;
  const pxPerMm = block.canvas.height / totalMm;
  // A picture that fits on a fresh page never starts on a page it can't finish.
  if (totalMm > flow.bottom - y && totalMm <= flow.bottom - MARGIN_MM && y > MARGIN_MM) y = flow.newPage();
  let srcY = 0;
  while (srcY < block.canvas.height - 1) {
    const room = flow.bottom - y;
    if (room < 15) {
      y = flow.newPage();
      continue;
    }
    const leftPx = block.canvas.height - srcY;
    let slicePx = Math.min(leftPx, Math.floor(room * pxPerMm));
    if (slicePx < leftPx) slicePx = Math.max(1, quietCut(block.canvas, srcY, slicePx));
    const sliceMm = slicePx / pxPerMm;
    pdf.addImage(sliceToJpeg(block.canvas, srcY, slicePx), "JPEG", x, y, wMm, sliceMm, undefined, "FAST");
    srcY += slicePx;
    y += sliceMm;
    if (srcY < block.canvas.height - 1) y = flow.newPage();
  }
  return y;
}

type JsPdfClass = typeof import("jspdf").jsPDF;

/** The sections as units: DOM blocks split into their pictures and tables
 *  (capture loads only when there is one), empty tables and texts dropped. */
async function collectUnits(input: ReportPdfInput): Promise<Unit[]> {
  const sections: PdfSection[] = [
    ...(input.blocks ?? []).map((element): PdfSection => ({ kind: "dom", element })),
    ...(input.sections ?? []),
  ];
  const domTotal = sections.filter((s) => s.kind === "dom").length;
  const segmentBlock = domTotal ? (await import("./capture")).segmentBlock : null;
  let domDone = 0;
  const units: Unit[] = [];
  // A chapter that starts on a section with nothing to print starts on the
  // next unit instead, so its contents line still lands on its pages.
  let pendingChapter: string | undefined;
  const push = (unit: Unit) => {
    const chapter = unit.chapter ?? pendingChapter;
    pendingChapter = undefined;
    units.push(chapter ? { ...unit, chapter } : unit);
  };
  for (const section of sections) {
    if (section.chapter) pendingChapter = section.chapter;
    if (section.kind === "dom") {
      const el = section.element;
      if (segmentBlock && el.isConnected && el.getBoundingClientRect().height >= 1) {
        for (const seg of await segmentBlock(el)) {
          if (seg.kind === "image") push({ kind: "image", block: seg.block });
          else if (hasRows(seg.table)) push({ kind: "table", table: seg.table, title: seg.title });
        }
      }
      input.onProgress?.(++domDone, domTotal);
      await yieldToBrowser();
    } else if (section.kind === "table") {
      if (hasRows(section.table)) push({ ...section, chapter: undefined });
    } else if (section.lines.length) {
      push({ ...section, chapter: undefined });
    }
  }
  return units;
}

/** One unit from y down, flowing across pages of the current format. */
async function drawUnit(pdf: jsPDF, unit: Unit, y: number, contentW: number, flow: Flow): Promise<number> {
  const bottom = flow.bottom;
  if (unit.kind === "text") {
    if (y + 14 > bottom) y = flow.newPage();
    const lines: TextLine[] = [
      ...(unit.heading ? [{ text: unit.heading, pt: 10.5, bold: true, color: INK, gapAfter: 1.2 } as TextLine] : []),
      ...unit.lines.map((text): TextLine => ({ text, pt: 8.5, color: MUTED, gapAfter: 0.8 })),
    ];
    return drawTextBlock(pdf, lines, MARGIN_MM, y, contentW, flow) + GAP_MM;
  }
  if (unit.kind === "table") {
    const plan = planColumns(pdf, unit.table, contentW);
    // A title or note stays with its table's first rows: start a fresh
    // page rather than strand them at the foot of one.
    const need = (unit.title ? 6 : 0) + (unit.note ? 5 : 0) + 28;
    if (y + need > bottom && y > MARGIN_MM + 1) y = flow.newPage();
    if (unit.title) y = drawTextBlock(pdf, [{ text: unit.title, pt: 10.5, bold: true, color: INK, gapAfter: 1.2 }], MARGIN_MM, y, contentW, flow);
    if (unit.note) y = drawTextBlock(pdf, [{ text: unit.note, pt: 7.5, color: FAINT, gapAfter: 1.8 }], MARGIN_MM, y, contentW, flow);
    return drawPdfTable(pdf, unit.table, { x: MARGIN_MM, y, width: contentW, bottom, top: MARGIN_MM, plan }) + GAP_MM;
  }
  return drawImage(pdf, unit.block, MARGIN_MM, y, contentW, flow) + GAP_MM;
}

/** The title and the filters it was cut with, under a rule. */
function drawHeader(pdf: jsPDF, input: ReportPdfInput, y: number, contentW: number, flow: Flow, extra: TextLine[] = []): number {
  const header: TextLine[] = [
    { text: input.title, pt: 15, bold: true, color: INK, gapAfter: 1.5 },
    ...input.summary.map((s): TextLine => ({ text: s, pt: 8.5, color: MUTED, gapAfter: 0.6 })),
    ...extra,
  ];
  y = drawTextBlock(pdf, header, MARGIN_MM, y, contentW, flow) + 1.5;
  pdf.setDrawColor(RULE[0], RULE[1], RULE[2]);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN_MM, y, MARGIN_MM + contentW, y);
  return y + 4;
}

/** FLOW: one run of A4 pages, landscape when anything wants the width. */
async function layoutFlow(JsPdf: JsPdfClass, input: ReportPdfInput, units: Unit[]): Promise<jsPDF> {
  // Landscape when a picture is wide (a whole report tab, a KPI strip) or a
  // table wants more than a portrait page; portrait otherwise.
  const probe = new JsPdf({ unit: "mm", format: "a4" });
  const landscape =
    units.some((u) => u.kind === "image" && u.block.cssWidth >= 900) ||
    units.some((u) => u.kind === "table" && planColumns(probe, u.table, PORTRAIT_CONTENT_MM).preferred > PORTRAIT_CONTENT_MM);

  const pdf = new JsPdf({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a4", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN_MM * 2;
  const bottom = pageH - MARGIN_MM - FOOTER_MM;
  let y = MARGIN_MM;
  const flow: Flow = {
    bottom,
    newPage: () => {
      pdf.addPage();
      y = MARGIN_MM;
      return y;
    },
  };

  y = drawHeader(pdf, input, y, contentW, flow);
  for (const unit of units) {
    y = await drawUnit(pdf, unit, y, contentW, flow);
    await yieldToBrowser();
  }
  return pdf;
}

/**
 * A chapter's page, [width, height] mm: A4 portrait when it fits, A4
 * landscape when its widest table fits there at its columns' minimums (or a
 * picture is wide), else a page as wide as that table at its preferred
 * widths — A-series proportions, so it still zooms and prints like a sheet.
 */
function chapterFormat(pdf: jsPDF, units: Unit[]): [number, number] {
  let preferred = 0;
  let minimum = 0;
  let wideImage = false;
  for (const u of units) {
    if (u.kind === "table") {
      const plan = planColumns(pdf, u.table, PORTRAIT_CONTENT_MM);
      preferred = Math.max(preferred, plan.preferred);
      minimum = Math.max(minimum, plan.minimum);
    } else if (u.kind === "image" && u.block.cssWidth >= 900) {
      wideImage = true;
    }
  }
  if (!wideImage && preferred <= PORTRAIT_CONTENT_MM) return [210, 297];
  if (minimum <= LANDSCAPE_CONTENT_MM) return [297, 210];
  const width = Math.ceil(Math.min(MAX_BOOK_CONTENT_MM, preferred) + MARGIN_MM * 2);
  return [width, Math.max(210, Math.ceil(width / Math.SQRT2))];
}

/** BOOK: cover + contents, a chapter per page run, bookmarks. */
async function layoutBook(JsPdf: JsPdfClass, input: ReportPdfInput, units: Unit[]): Promise<jsPDF> {
  // Page 1 is the cover; it is drawn last, once every chapter's page is known.
  const pdf = new JsPdf({ orientation: "portrait", unit: "mm", format: "a4", compress: true });

  const chapters: { name: string; units: Unit[] }[] = [];
  for (const unit of units) {
    if (unit.chapter || !chapters.length) chapters.push({ name: unit.chapter ?? input.title, units: [] });
    chapters[chapters.length - 1]!.units.push(unit);
  }

  const starts: { name: string; page: number }[] = [];
  for (const chapter of chapters) {
    const [w, h] = chapterFormat(pdf, chapter.units);
    // Every later page of the chapter — a text break, autotable's own — is a
    // bare addPage(), which jsPDF gives the last page's format.
    pdf.addPage([w, h], w > h ? "landscape" : "portrait");
    starts.push({ name: chapter.name, page: pdf.getNumberOfPages() });
    const contentW = w - MARGIN_MM * 2;
    let y = MARGIN_MM;
    const flow: Flow = {
      bottom: h - MARGIN_MM - FOOTER_MM,
      newPage: () => {
        pdf.addPage();
        y = MARGIN_MM;
        return y;
      },
    };
    y = drawTextBlock(pdf, [{ text: chapter.name, pt: 13, bold: true, color: INK, gapAfter: 1.2 }], MARGIN_MM, y, contentW, flow) + 1.2;
    pdf.setDrawColor(RULE[0], RULE[1], RULE[2]);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN_MM, y, MARGIN_MM + contentW, y);
    y += 4;
    for (const unit of chapter.units) {
      y = await drawUnit(pdf, unit, y, contentW, flow);
      await yieldToBrowser();
    }
  }

  // The cover: title, filters, the hint, then the contents.
  pdf.setPage(1);
  const coverW = 210 - MARGIN_MM * 2;
  const coverBottom = 297 - MARGIN_MM - FOOTER_MM;
  // The cover never grows a page (the chapters follow it): a summary long
  // enough to reach the footer runs on down the page, one line at a time,
  // rather than renumber the chapters.
  const still: Flow = { bottom: coverBottom, newPage: () => coverBottom };
  let y = MARGIN_MM;
  const hint: TextLine[] = input.hint ? [{ text: input.hint, pt: 8.5, bold: true, color: LINK_INK, gapAfter: 0.6 }] : [];
  y = drawHeader(pdf, input, y, coverW, still, hint);
  y = drawTextBlock(
    pdf,
    [
      { text: "Contents", pt: 11, bold: true, color: INK, gapAfter: 0.8 },
      { text: "Tap a line to go to it.", pt: 7.5, color: FAINT, gapAfter: 2 },
    ],
    MARGIN_MM,
    y,
    coverW,
    still,
  );
  const lh = lineHeightMm(10);
  starts.forEach((start, i) => {
    const pageLabel = `Page ${start.page}`;
    setFont(pdf, 10, false, MUTED);
    const pageW = pdf.getTextWidth(pageLabel);
    const name = wrapText(pdf, `${i + 1}.  ${start.name}`, 10, true, coverW - pageW - 6)[0] ?? start.name;
    drawLines(pdf, [name], MARGIN_MM, y, 10, { bold: true, color: LINK_INK, widthMm: coverW - pageW - 6 });
    drawLines(pdf, [pageLabel], MARGIN_MM, y, 10, { color: MUTED, align: "right", widthMm: coverW });
    pdf.link(MARGIN_MM, y - 0.8, coverW, lh + 1.6, { pageNumber: start.page });
    y += lh + 2.4;
  });

  pdf.outline.add(null, "Contents", { pageNumber: 1 });
  for (const start of starts) pdf.outline.add(null, start.name, { pageNumber: start.page });
  return pdf;
}

/** "Prepared by … · <IST> · Xale CRM" and "Page n of N" on every page, each
 *  at its own page's size (book chapters can be wider than A4). */
function drawFooters(pdf: jsPDF, preparedBy?: string | null): void {
  const pageCount = pdf.getNumberOfPages();
  const left = [preparedBy ? `Prepared by ${preparedBy}` : null, istStamp(), "Xale CRM"]
    .filter(Boolean)
    .join(" · ");
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const contentW = pageW - MARGIN_MM * 2;
    const right = `Page ${p} of ${pageCount}`;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
    const fy = pageH - MARGIN_MM - 2;
    pdf.text(right, pageW - MARGIN_MM, fy, { align: "right", baseline: "bottom" });
    const rightW = pdf.getTextWidth(right);
    let text = left;
    while (text.length > 4 && pdf.getTextWidth(text) > contentW - rightW - 6) text = `${text.slice(0, -2).trimEnd()}…`;
    pdf.text(text, MARGIN_MM, fy, { baseline: "bottom" });
  }
}

/** The laid-out document, before it becomes a file (tests read it back). */
export async function layoutReportPdf(input: ReportPdfInput): Promise<{ pdf: jsPDF; pageCount: number }> {
  const { jsPDF: JsPdf } = await import("jspdf");
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    /* best effort — capture with whatever is painted */
  }
  const units = await collectUnits(input);
  if (!units.length) throw new Error("There is nothing on screen to put in the PDF yet.");
  const pdf = input.layout === "book" ? await layoutBook(JsPdf, input, units) : await layoutFlow(JsPdf, input, units);
  drawFooters(pdf, input.preparedBy);
  return { pdf, pageCount: pdf.getNumberOfPages() };
}

export async function buildReportPdf(input: ReportPdfInput): Promise<PreparedPdf> {
  const { pdf, pageCount } = await layoutReportPdf(input);
  return {
    blob: pdf.output("blob"),
    fileName: reportPdfFileName(input.title),
    title: input.title,
    summary: input.summary,
    pageCount,
  };
}

/** Save a prepared PDF through the browser's normal download. */
export function downloadPreparedPdf(pdf: PreparedPdf): void {
  const url = URL.createObjectURL(pdf.blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = pdf.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
